import moment from 'moment-timezone';
import superagent from 'superagent';
import config from '../config';
import apiToken from './ApiToken';
import { randomString } from '../../shared/lib/random';

const methods = ['get', 'post', 'put', 'patch', 'del'];

function formatUrl(path) {
  const adjustedPath = `/v2/api${(path[0] !== '/' ? `/${path}` : path)}`;

  if (__SERVER__) {
    // Prepend host and port of the API server to the path.
    return `http://${config.apiHost}:${config.apiPort}${adjustedPath}`;
  }

  return adjustedPath;
}

class ApiClient {
  serverTimeDiffInSecs = null;
  csrfToken = null;

  constructor(req) {
    this.req = req;
    if (req) {
      this.setCSRFToken(req.csrf);
    }

    methods.forEach((method) => {
      this[method] = (path, { params, data } = {}) => {
        const url = formatUrl(path);
        const token = apiToken(this.serverTimeDiffInSecs, false);
        const rqid = randomString();

        console.log(`🚀 ApiClient: ${method.toUpperCase()} ${url}`);

        // Server-side: simple request without WAF handling
        if (__SERVER__) {
          console.log('📡 Server-side request, no WAF handling needed');
          return this.makeBasicRequest(method, url, token, rqid, params, data);
        }

        // Client-side: with WAF handling
        console.log('💻 Client-side request, WAF handling enabled');
        return this.makeClientRequest(method, url, token, rqid, params, data);
      };
    });
  }

  makeBasicRequest(method, url, token, rqid, params, data) {
    return new Promise((resolve, reject) => {
      const request = superagent[method](url);
      request.set('Authorization', `Bearer ${token}`);
      request.set('CSRF-Token', this.csrfToken);
      request.query({ rqid });
      
      if (params) request.query(params);
      if (this.req && this.req.get('cookie')) {
        request.set('cookie', this.req.get('cookie'));
      }
      if (data) request.send(data);
      
      request.end((err, res) => {
        if (err) {
          console.error('❌ Basic request failed:', err.status, err.message);
          reject(err);
        } else {
          console.log('✅ Basic request successful');
          resolve(res.body);
        }
      });
    });
  }

  async makeClientRequest(method, url, token, rqid, params, data) {
    let wafToken = null;
    
    // Try to get WAF token
    try {
      // eslint-disable-next-line no-undef
      if (typeof AwsWafIntegration !== 'undefined' && AwsWafIntegration.getToken) {
        wafToken = await AwsWafIntegration.getToken();
        console.log('🛡️ WAF token obtained:', wafToken ? 'Yes' : 'No');
      } else {
        console.log('⚠️ AwsWafIntegration not available');
      }
    } catch (error) {
      console.warn('⚠️ WAF token acquisition failed:', error.message);
    }

    // First attempt with current token
    try {
      const result = await this.makeRequestWithToken(method, url, token, rqid, params, data, wafToken);
      console.log('✅ Request successful on first attempt');
      return result;
    } catch (error) {
      console.log(`❌ First attempt failed: ${error.status} ${error.message}`);
      
      // If it's a WAF challenge, try with WAF Integration
      if (error.status === 405 || error.status === 403) {
        console.log('🔄 Attempting WAF CAPTCHA challenge resolution...');
        return this.handleWafChallenge(method, url, token, rqid, params, data, error);
      }
      
      throw error;
    }
  }

  makeRequestWithToken(method, url, token, rqid, params, data, wafToken) {
    return new Promise((resolve, reject) => {
      const request = superagent[method](url);
      request.set('Authorization', `Bearer ${token}`);
      request.set('CSRF-Token', this.csrfToken);
      request.query({ rqid });
      
      if (params) request.query(params);
      if (wafToken) {
        request.set('X-Aws-Waf-Token', wafToken);
        console.log('🛡️ Added WAF token to request');
      }
      if (data) request.send(data);
      
      request.end((err, res) => {
        if (err) {
          reject(err);
        } else {
          resolve(res.body);
        }
      });
    });
  }

  async handleWafChallenge(method, url, token, rqid, params, data, originalError) {
    try {
      // eslint-disable-next-line no-undef
      if (typeof AwsWafIntegration === 'undefined' || !AwsWafIntegration.fetch) {
        console.error('❌ AwsWafIntegration.fetch not available for CAPTCHA handling');
        throw originalError;
      }

      console.log('🎯 Using AwsWafIntegration.fetch for CAPTCHA challenge');

      // Build full URL with parameters
      let fullUrl = url.startsWith('/') ? window.location.origin + url : url;
      const urlObj = new URL(fullUrl);
      urlObj.searchParams.set('rqid', rqid);
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          urlObj.searchParams.set(key, value);
        });
      }

      // Prepare request options
      const requestOptions = {
        method: method.toUpperCase(),
        headers: {
          'Authorization': `Bearer ${token}`,
          'CSRF-Token': this.csrfToken,
        },
      };

      if (data) {
        if (typeof data === 'object' && !(data instanceof FormData)) {
          requestOptions.headers['Content-Type'] = 'application/json';
          requestOptions.body = JSON.stringify(data);
        } else {
          requestOptions.body = data;
        }
      }

      console.log('🔄 Making WAF-handled request...');
      const wafResponse = await AwsWafIntegration.fetch(urlObj.toString(), requestOptions);
      
      if (wafResponse.ok) {
        console.log('✅ WAF CAPTCHA challenge resolved successfully');
        const contentType = wafResponse.headers.get('content-type') || '';
        return contentType.includes('application/json') 
          ? await wafResponse.json() 
          : await wafResponse.text();
      } else {
        console.error('❌ WAF request failed:', wafResponse.status);
        throw new Error(`WAF request failed with status ${wafResponse.status}`);
      }
    } catch (captchaError) {
      console.error('❌ CAPTCHA resolution failed:', captchaError.message);
      throw originalError;
    }
  }

  setServerTime(serverTime) {
    if (serverTime) {
      this.serverTimeDiffInSecs = moment.utc().unix() - serverTime;
    }
  }

  setCSRFToken(token) {
    this.csrfToken = token;
  }
}

export default ApiClient;
